<script>
import { mapGetters } from 'vuex';
import { hasPressedCommand } from 'shared/helpers/KeyboardHelpers';
import GalleryView from '../components/GalleryView.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  IG_REEL: 'ig_reel',
};

export default {
  components: {
    GalleryView,
  },
  props: {
    attachment: {
      type: Object,
      required: true,
    },
    isPending: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['error', 'loaded'],
  data() {
    return {
      show: false,
      isImageError: false,
      videoDuration: 0,
    };
  },
  computed: {
    ...mapGetters({
      currentChatAttachments: 'getSelectedChatAttachments',
    }),
    isImage() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.IMAGE;
    },
    isVideo() {
      return (
        this.attachment.file_type === ALLOWED_FILE_TYPES.VIDEO ||
        this.attachment.file_type === ALLOWED_FILE_TYPES.IG_REEL
      );
    },
    isAudio() {
      return this.attachment.file_type === ALLOWED_FILE_TYPES.AUDIO;
    },
    timeStampURL() {
      if (!this.dataUrl || this.dataUrl.startsWith('data:')) {
        return this.dataUrl;
      }
      return timeStampAppendedURL(this.dataUrl);
    },
    attachmentTypeClasses() {
      return {
        image: this.isImage,
        video: this.isVideo,
      };
    },
    filteredCurrentChatAttachments() {
      const attachments = this.currentChatAttachments.filter(attachment =>
        ['image', 'video', 'audio'].includes(attachment.file_type)
      );
      return attachments;
    },
    dataUrl() {
      return this.attachment.data_url;
    },
    imageWidth() {
      return this.attachment.width ? `${this.attachment.width}px` : 'auto';
    },
    imageHeight() {
      return this.attachment.height ? `${this.attachment.height}px` : 'auto';
    },
    formattedDuration() {
      if (!this.videoDuration || !isFinite(this.videoDuration)) return '';
      const total = Math.round(this.videoDuration);
      const mins = Math.floor(total / 60);
      const secs = total % 60;
      return `${mins}:${String(secs).padStart(2, '0')}`;
    },
  },
  watch: {
    attachment() {
      this.isImageError = false;
    },
  },
  methods: {
    onClose() {
      this.show = false;
    },
    onClick(e) {
      if (this.isPending) return;
      if (hasPressedCommand(e)) {
        window.open(this.attachment.data_url, '_blank');
        return;
      }
      this.show = true;
    },
    onImgError() {
      this.isImageError = true;
      this.$emit('error');
    },
    onMediaLoaded() {
      this.$emit('loaded');
    },
    onVideoMetadata(event) {
      const dur = event?.target?.duration;
      if (dur && isFinite(dur)) this.videoDuration = dur;
      this.onMediaLoaded();
    },
  },
};
</script>

<template>
  <div
    class="message-text__wrap"
    :class="[attachmentTypeClasses, { 'is-pending-media': isPending }]"
  >
    <img
      v-if="isImage && !isImageError"
      class="bg-woot-200 dark:bg-woot-900"
      :src="dataUrl"
      :width="imageWidth"
      :height="imageHeight"
      @click="onClick"
      @error="onImgError"
      @load="onMediaLoaded"
    />
    <div v-if="isVideo" class="video-wrap">
      <video
        :src="dataUrl"
        muted
        playsInline
        preload="metadata"
        @error="onImgError"
        @click="onClick"
        @loadedmetadata="onVideoMetadata"
      />
      <div class="video-meta-badge">
        <span class="i-lucide-video video-meta-icon" />
        <span v-if="formattedDuration" class="video-meta-time">{{
          formattedDuration
        }}</span>
      </div>
      <div class="video-play-circle">
        <span class="i-lucide-play video-play-icon" />
      </div>
    </div>
    <audio
      v-else-if="isAudio"
      controls
      class="skip-context-menu mb-0.5"
      @loadedmetadata="onMediaLoaded"
    >
      <source :src="timeStampURL" />
    </audio>
    <GalleryView
      v-if="show && !isPending"
      v-model:show="show"
      :attachment="attachment"
      :all-attachments="filteredCurrentChatAttachments"
      @error="onImgError"
      @close="onClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.video-wrap {
  @apply relative inline-flex items-center justify-center;
  max-width: 100%;
  max-height: 100%;
}

.video-wrap > video {
  @apply block;
  max-width: 100%;
  max-height: 20rem;
  width: auto;
  height: auto;
}

.video-play-circle {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full pointer-events-none;
  width: 56px;
  height: 56px;
  background-color: rgba(0, 0, 0, 0.55);
}

.video-play-icon {
  width: 28px;
  height: 28px;
  color: #ffffff;
}

.video-meta-badge {
  @apply absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded pointer-events-none;
  background-color: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  font-size: 11px;
  line-height: 1;
}

.video-meta-icon {
  width: 12px;
  height: 12px;
  color: #ffffff;
}

.video-meta-time {
  color: #ffffff;
  font-variant-numeric: tabular-nums;
}

.is-pending-media {
  @apply relative;
}
</style>
